import {
  ActionIcon, Card, DetailStack, HStack, Icon, ICON_LINK, ICON_MAP_MARKER_OUTLINE, ICON_PENCIL_OUTLINE, Link, Stack, Text,
  UserPreview,
} from '@doveit/bricks';
import React from 'react';
import { HRInterview, JobApplication } from '../../../providers/api/dtos';
import DateViewer from '../../../components/date-viewer/DateViewer';
import UpsertHRInterviewAction, { UpsertHRInterviewActionProps } from '../../../job-application/containers/upsert-hr-interview-action/UpsertHRInterviewAction';
import HRInterviewAvatar from '../../components/hr-interview-avatar/HRInterviewAvatar';
import UpdateHRInterviewStatus from '../update-hr-interview-status/UpdateHRInterviewStatus';
import { jobApplicationAssigneeUsers, jobApplicationHrUsers } from '../../../job-application/constants';
import { JobApplicationType } from '../../../domain/types';
import { ViewAgentSmallPreview } from '../../../agent/components';
import { jobTitleLabels } from '../../../labels';

export interface ViewHRInterviewCardProps extends React.AriaAttributes {
  hrInterview: HRInterview,
  jobApplication: JobApplication,
  canUpdate?: boolean,
  onUpdate?: UpsertHRInterviewActionProps['onSuccess'];
}

const ViewHRInterviewCard: React.FC<ViewHRInterviewCardProps> = ({
  hrInterview,
  jobApplication,
  canUpdate = false,
  onUpdate,
  ...rest
}) => {
  const extraParticipant = React.useMemo(() => {
    if (!hrInterview?.extraParticipantEmail) return undefined;
    return jobApplicationHrUsers.find((hrUser) => hrUser.email === hrInterview?.extraParticipantEmail);
  }, [hrInterview]);

  return (
    <Card
      aria-label="view-hr-recall-card"
      {...rest}
    >
      <Card.Header
        primaryActions={[
          canUpdate && (
            <UpsertHRInterviewAction
              jobApplication={jobApplication}
              hrInterview={hrInterview}
              canUpdate={canUpdate}
              onSuccess={onUpdate}
            >
              {({ openModal, isSaving }) => (
                <ActionIcon
                  icon={{ path: ICON_PENCIL_OUTLINE }}
                  label="Modifica colloquio"
                  aria-label="Azione per modificare il colloquio"
                  onClick={openModal}
                  disabled={isSaving}
                  size="S"
                />
              )}
            </UpsertHRInterviewAction>
          ),
          (hrInterview.isRemote && hrInterview.meetingLink) && (
            <ActionIcon
              icon={{ path: ICON_LINK }}
              label="Accedi al meet del colloquio"
              aria-label="Azione per accedere al meet del colloquio"
              href={hrInterview.meetingLink}
              target="_blank"
              size="S"
            />
          ),
        ]}
      >
        <HStack gap={100} wrap="nowrap">
          <HRInterviewAvatar size="S" />
          <DetailStack label={hrInterview.isRemote ? 'Colloquio da remoto' : 'Colloquio in presenza'}>
            <DateViewer
              stringDate={hrInterview.date}
              withHour
              checkIfToday
              withStrong
            />
          </DetailStack>
        </HStack>
      </Card.Header>
      <Card.Content>
        <Stack gap={150}>
          <div>
            <UpdateHRInterviewStatus
              hrInterview={hrInterview}
              showAsBadge={!canUpdate}
            />
          </div>
          {jobApplication.applicationType === JobApplicationType.SALES && (
            <ViewAgentSmallPreview
              agentIdOrEmail={hrInterview.interviewerEmail}
              details={(agent) => `Esaminatore - ${jobTitleLabels[agent.jobTitle]}`}
              size="S"
            />
          )}
          {jobApplication.applicationType !== JobApplicationType.SALES && (
            <UserPreview
              name={[...jobApplicationAssigneeUsers, ...jobApplicationHrUsers].find((executiveUser) => executiveUser.email === hrInterview?.interviewerEmail)?.name}
              size="S"
              aria-label="Anteprima esaminatore"
            >
              Esaminatore - HR
            </UserPreview>
          )}
          {extraParticipant && (
            <UserPreview
              name={extraParticipant.name}
              size="S"
              aria-label="Partecipante HR"
            >
              Partecipante - HR
            </UserPreview>
          )}
          {(hrInterview.isRemote && hrInterview.meetingLink) && (
            <HStack wrap="nowrap" gap={75}>
              <Icon
                path={ICON_LINK}
                size={20}
              />
              <Text.Body as="div">
                <Link
                  aria-label="Link al meet del colloquio"
                  href={hrInterview.meetingLink}
                  target="_blank"
                >
                  {hrInterview.meetingLink}
                </Link>
              </Text.Body>
            </HStack>
          )}
          {!hrInterview.isRemote && hrInterview.address?.normalizedAddress && (
            <HStack wrap="nowrap" gap={75}>
              <Icon
                path={ICON_MAP_MARKER_OUTLINE}
                size={20}
              />
              <Text.Body>
                {hrInterview.address.normalizedAddress}
              </Text.Body>
            </HStack>
          )}
          {hrInterview.note && (
            <Text.BodySmall aria-label="Nota del colloquio">
              {hrInterview.note}
            </Text.BodySmall>
          )}
        </Stack>
      </Card.Content>
    </Card>
  );
};

export default ViewHRInterviewCard;
